import type { GetTransactionsResponse } from "../../../../types/transactions";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import type {
  BadgeDisplayMode,
  BadgeSettings,
} from "../../../../utils/badgeSettings";
import { isAwaitingConfirmation } from "../booking/guards";
import type { EventFlag } from "../components/Views/EventBlock";

/**
 * The "Need to know" flags (design handoff; prototype `flagsFor`), ordered
 * most → least urgent. Pure and shared: desktop event chips, the appointment
 * drawer's Need to know rows, List / Card rows and the mobile blocks / detail
 * sheet all call this.
 *
 * Always on: requested, arrived, unconfirmed (#580 display state), confirmed,
 * forms missing, tags (prototype `ALWAYS_KEYS`). Toggleable (Advanced
 * Settings, `show_badge_*`): no-show history, pinned notes, opted out of
 * reminders / marketing, campaign, loyal. None / Badge / Badge + Label
 * (Advanced Settings, `badge_display_*`): any available, then new client —
 * in Label mode the flag carries `chipLabel`, shown beside the chip's icon
 * where the event has room (EventFlags). Out: frequent reschedules (no data).
 */

const CLOSED = new Set(["Completed", "No Show", "Declined", "Cancelled"]);
const TWO_DAYS_MINS = 48 * 60;

export interface FlagContext {
  /** The appointment (status, forms, client, client_flags). */
  txn: GetTransactionsResponse | null | undefined;
  /** Block status (falls back to the transaction's). */
  status?: string | null;
  /** Grid times: fake-local salon wall-clock, like `now`. */
  start: Date;
  end: Date;
  /** Salon "now" as a fake-local Date (getSalonNow). */
  now: Date;
  /** Salon timezone (the unconfirmed display state compares instants). */
  timezone: string;
  settings: BadgeSettings;
  /** The real current instant (defaults to new Date()). */
  instant?: Date;
}

/** Badge + Label mode: the short word beside the chip's icon. */
const chipLabelFor = (mode: BadgeDisplayMode, text: string) =>
  mode === "label" ? { chipLabel: text } : {};

export function calendarFlags(ctx: FlagContext): EventFlag[] {
  const { txn, start, end, now, timezone, settings } = ctx;
  const status = ctx.status ?? txn?.transaction?.appointment_status ?? "";
  const open = !CLOSED.has(status);
  const minsAway = (start.getTime() - now.getTime()) / 60000;
  const durMins = (end.getTime() - start.getTime()) / 60000;
  const client = txn?.client;
  const cf = txn?.transaction?.client_flags ?? null;
  const out: EventFlag[] = [];

  if (open && status === "Requested") {
    out.push({
      key: "requested",
      Icon: DESIGN_ICONS.Hourglass,
      label: "Request — needs approval",
      detail: "Booked online and waiting for you to accept or decline.",
      tone: "warn",
    });
  }
  const anyMode = settings.badge_display_any_available;
  if (anyMode !== "none" && txn?.transaction?.booked_using_any_available) {
    out.push({
      key: "anyAvailable",
      Icon: DESIGN_ICONS.Shuffle,
      label: "Any available",
      detail:
        "Booked with the “Any Available” option, not a specific staff member.",
      tone: "warn",
      ...chipLabelFor(anyMode, "Any"),
    });
  }
  const newClientMode = settings.badge_display_new_client;
  if (newClientMode !== "none" && txn?.transaction?.created_new_client) {
    out.push({
      key: "newClient",
      Icon: DESIGN_ICONS.User,
      label: "New client",
      detail:
        "First appointment with you — allow time for intake and a consult.",
      tone: "good",
      ...chipLabelFor(newClientMode, "New"),
    });
  }
  const arrived = status === "Arrived";
  if (arrived) {
    out.push({
      key: "arrived",
      Icon: DESIGN_ICONS.UserCheck,
      label: "Arrived",
      detail: "Checked in and waiting.",
      tone: "good",
    });
  }
  if (
    txn &&
    open &&
    isAwaitingConfirmation(txn, timezone, ctx.instant ?? new Date())
  ) {
    out.push({
      key: "unconfirmed",
      Icon: DESIGN_ICONS.CircleDashed,
      label: "Not confirmed",
      detail: "Coming up within 48 hours and the client hasn’t confirmed.",
      tone: "alert",
    });
  }
  if (open && !arrived && minsAway > 0 && status === "Confirmed") {
    out.push({
      key: "confirmed",
      Icon: DESIGN_ICONS.CircleCheck,
      label: "Confirmed",
      detail: "The client confirmed this appointment.",
      tone: "info",
    });
  }
  const formsPending = (txn?.forms ?? []).some(
    (f) => f.status !== "completed" && f.status !== "expired",
  );
  if (
    formsPending &&
    open &&
    minsAway > -durMins &&
    minsAway <= TWO_DAYS_MINS
  ) {
    out.push({
      key: "forms",
      Icon: DESIGN_ICONS.FileWarning,
      label: "Forms missing",
      detail:
        "Required forms aren’t complete and the appointment is within 48 hours.",
      tone: "alert",
    });
  }
  const noShows = cf?.no_show_count_12mo ?? 0;
  if (settings.show_badge_no_show_history && noShows >= 1) {
    out.push({
      key: "history",
      Icon: DESIGN_ICONS.UserX,
      label: "No-show history",
      detail: `${noShows} ${noShows === 1 ? "no-show" : "no-shows"} in the last 12 months.`,
      tone: "alert",
    });
  }
  const pinned = cf?.pinned_note_count ?? 0;
  if (settings.show_badge_pinned_notes && pinned > 0) {
    out.push({
      key: "notes",
      Icon: DESIGN_ICONS.Pin,
      label: pinned === 1 ? "Pinned note" : `${pinned} pinned notes`,
      detail: "Pinned on the client's profile.",
      tone: "warn",
    });
  }
  const tags = txn?.tags ?? [];
  if (tags.length > 0) {
    out.push({
      key: "tags",
      Icon: DESIGN_ICONS.Tag,
      label: tags.length === 1 ? "Tag" : `${tags.length} tags`,
      detail: tags
        .map((t) => (t.emoji ? `${t.emoji} ${t.name}` : t.name))
        .join(" · "),
      tone: "info",
    });
  }
  if (
    settings.show_badge_opt_out_reminder &&
    client?.is_opted_in_reminder === false
  ) {
    out.push({
      key: "noReminders",
      Icon: DESIGN_ICONS.BellOff,
      label: "No reminders",
      detail: "Opted out of appointment reminders and transactional messages.",
      tone: "muted",
    });
  }
  if (
    settings.show_badge_opt_out_marketing &&
    client?.is_opted_in_marketing === false
  ) {
    out.push({
      key: "noMarketing",
      Icon: DESIGN_ICONS.MailX,
      label: "No marketing",
      detail: "Opted out of marketing messages.",
      tone: "muted",
    });
  }
  if (settings.show_badge_campaign && cf?.campaign_name) {
    out.push({
      key: "campaign",
      Icon: DESIGN_ICONS.Megaphone,
      label: "From a campaign",
      detail: `Came from the “${cf.campaign_name}” campaign.`,
      tone: "info",
    });
  }
  const visits = cf?.completed_visit_count ?? 0;
  if (settings.show_badge_loyal && visits >= 5) {
    out.push({
      key: "loyal",
      Icon: DESIGN_ICONS.Heart,
      label: "Loyal client",
      detail: `${visits} visits so far.`,
      tone: "good",
    });
  }
  return out;
}

/**
 * The drawer knows the pinned notes themselves: the "notes" flag shows the
 * first one (+N more) instead of the count-only detail, and appears even
 * before the backend's count ships. Respects the pinned-notes toggle.
 */
export function withPinnedNotes(
  flags: EventFlag[],
  pinnedNotes: string[],
  settings: BadgeSettings,
): EventFlag[] {
  if (!settings.show_badge_pinned_notes || pinnedNotes.length === 0) {
    return flags;
  }
  const notes: EventFlag = {
    key: "notes",
    Icon: DESIGN_ICONS.Pin,
    label:
      pinnedNotes.length === 1
        ? "Pinned note"
        : `${pinnedNotes.length} pinned notes`,
    detail:
      pinnedNotes[0] +
      (pinnedNotes.length > 1 ? ` +${pinnedNotes.length - 1} more` : ""),
    tone: "warn",
  };
  const at = flags.findIndex((f) => f.key === "notes");
  if (at >= 0) return flags.map((f, i) => (i === at ? notes : f));
  // Keep the prototype order: after no-show history, before tags / opt-outs.
  const before = flags.findIndex((f) =>
    ["tags", "noReminders", "noMarketing", "campaign", "loyal"].includes(f.key),
  );
  const out = [...flags];
  out.splice(before < 0 ? out.length : before, 0, notes);
  return out;
}

/**
 * The drawer / mobile sheet show tags in their own Tags row, so Need to know
 * drops the tags flag (prototype filters `key !== 'tags'`).
 */
export const withoutTagsFlag = (flags: EventFlag[]): EventFlag[] =>
  flags.filter((f) => f.key !== "tags");

/** Flags for a whole appointment (List / Card rows, the drawer), from its
 *  booked services' true instants. */
export function transactionFlags(
  txn: GetTransactionsResponse,
  timezone: string,
  settings: BadgeSettings,
  instant: Date = new Date(),
): EventFlag[] {
  const services = txn.line_items?.booked_services ?? [];
  const starts = services
    .map((s) => Date.parse(s.start_dt))
    .filter((n) => !Number.isNaN(n));
  const ends = services
    .map((s) => Date.parse(s.calc__end_dt))
    .filter((n) => !Number.isNaN(n));
  if (!starts.length) return [];
  return calendarFlags({
    txn,
    start: new Date(Math.min(...starts)),
    end: new Date(ends.length ? Math.max(...ends) : Math.min(...starts)),
    now: instant,
    instant,
    timezone,
    settings,
  });
}
